import './Admin-login.css';
import React, { useState, useCallback } from 'react';
import { useToast } from '../../Utils/toastContext';
import { debounce } from 'lodash';
import CustomerLeftPanel from '../../Components/CustomerLeftPanel/customer-left-panel';
import { useNavigate } from 'react-router-dom';
import ContactUs from '../../Pages/Contact-Us/contact-us';
import { useDispatch } from 'react-redux';
import { clearForcedPassword } from '../../Redux/store/userSlice';
const ChangePassword = () => {
  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const { showToast } = useToast();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSupportOpen, setIsSupportOpen] = useState(false);

  const togglePassword = () => {
    setShowPassword((prevState) => !prevState);
  };
  const toggleConfirmPassword = () => {
    setShowConfirmPassword((prevState) => !prevState);
  };

  const calculateStrength = (password) => {
    let score = 0;

    // Conditions
    if (password.length >= 8) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[^A-Za-z0-9]/.test(password)) score++;

    return score;
  };

  const strengthScore = calculateStrength(password);
  const passwordsMatch = password === confirmPassword;
  const isValid = strengthScore === 4 && passwordsMatch;

  const submitNewPassword = async (password) => {
    const apiUrl = `${BASE_URL}/api/v1/auth/app/set-password`;
    const headers = {
      'Content-Type': 'application/json',
    };

    // App session token — adjust the storage key if yours differs.
    const token = localStorage.getItem('jwtToken');
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const bodyData = {
      new_password: password,
    };

    try {
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(bodyData),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({
          message: 'An unexpected error occurred',
        }));

        throw new Error(errorData.message || 'Something went wrong');
      }

      return await response.json();
    } catch (error) {
      throw new Error(error.message || 'Network error, please try again later');
    }
  };

  const handleChangePassword = useCallback(
    debounce(async (e) => {
      e.preventDefault();
      let toastMessage = null;
      try {
        const response = await submitNewPassword(password);

        if (response.status == 200) {
          toastMessage = {
            message: 'Password Updated Successfully',
            type: 'success',
          };
          navigate('/login');
          dispatch(clearForcedPassword());
        } else {
          toastMessage = { message: response.message, type: 'error' };
          return;
        }
      } catch (err) {
        toastMessage = { message: err.message, type: 'error' };
        return;
      } finally {
        setLoading(false); // Ensure `loading` is false first
        // Show toast after loading is false
        if (toastMessage) {
          setTimeout(() => {
            showToast(toastMessage.message, toastMessage.type);
          }, 300); // Small delay if needed
        }
      }
    }, 1000),
    [password], // Dependencies
  );

  return (
    <div className="flex  flex-col lg:flex-row h-screen dark:bg-[#101010] bg-[#FFFFFF]">
      <CustomerLeftPanel />
      {loading && (
        <div className="fixed top-0 left-0 w-full h-full flex justify-center items-center bg-black bg-opacity-50 z-[999]">
          <div className="w-12 h-12 border-4 border-white border-t-transparent rounded-full animate-spin"></div>
        </div>
      )}
      <div className="w-full right-div lg:w-2/5 flex flex-col">
        <div className="dark:bg-[#101010]  bg-[#FAFAFA] flex justify-end py-[10px] px-[15px]">
          <button
            onClick={() => setIsSupportOpen(true)}
            className={`flex items-center bg-black  h-11 gap-2 px-3 py-2 text-xs rounded-xl transition-all duration-200 text-white shadow hover:scale-105`}
            style={{ background: '' }}
          >
            {/* Icon */}
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M18 13a3 3 0 01-3 3h-1v-4h1a3 3 0 013 3zM6 13a3 3 0 003 3h1v-4H9a3 3 0 00-3 3z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 3a9 9 0 00-9 9v3a3 3 0 003 3h1v-6H6a6 6 0 1112 0h-1v6h1a3 3 0 003-3v-3a9 9 0 00-9-9z"
              />
            </svg>

            {/* Text */}
            <span className="hidden sm:inline">Need Help?</span>
          </button>
        </div>
        {isSupportOpen && (
          <div className="fixed inset-0 z-[20] flex items-center justify-center bg-black/40 backdrop-blur-sm">
            {/* Modal */}
            <ContactUs
              onClose={() => setIsSupportOpen(false)}
              isLogin={false}
            />
          </div>
        )}
        <div className="flex flex-col mt-10 justify-center items-center md:p-0 p-10 justify">
          <h2 className="text-2xl mb-[32px] dark:text-white font-medium mb-6">
            Change Password
          </h2>

          <form
            className="w-full max-w-sm"
            onSubmit={(e) => {
              e.preventDefault();
              if (!password) {
                showToast('Please Enter your new password!', 'error');
                return;
              }
              if (!isValid) {
                showToast(
                  'Please make sure all password conditions are met and passwords match!',
                  'error',
                );
                return;
              }
              setLoading(true);
              handleChangePassword(e);
            }}
          >
            <p className="text-sm mb-[32px] dark:text-white font-medium mb-6">
              Choose a new password that meets the requirements below.
            </p>
            <div className="mb-4">
              <div className="relative mt-1">
                {/* Password Input Field */}
                <label
                  htmlFor="password"
                  className="block color-test dark:text-gray-300 font-normal color-test text-md mb-1"
                >
                  Password
                </label>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  required={true}
                  id="password"
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-[2.813rem] rounded-lg  appearance-none border w-full py-2 px-3  dark:focus-visible:ring-[#4E4E4E] dark:text-white focus-visible:ring-1 focus-visible:ring-black text-black text-base font-medium leading-tight focus:outline-none focus:shadow-outline"
                  placeholder="Type password"
                />
                <button
                  type="button"
                  onClick={togglePassword}
                  className="absolute right-3 set-top"
                >
                  {showPassword ? (
                    <img src="/eye.svg" alt="Logo" className="h-auto" />
                  ) : (
                    <img src="/eye-slash.svg" alt="Logo" className="h-auto" />
                  )}
                </button>

                <div className="display-none flex gap-1  absolute top-0 right-0 mt-4">
                  <div
                    className={
                      strengthScore > 0 && strengthScore < 2
                        ? 'bg-red-400 px-4 h-1 rounded-md'
                        : strengthScore >= 2 && strengthScore < 4
                          ? 'bg-yellow-400 px-4 h-1 rounded-md'
                          : strengthScore === 4
                            ? 'bg-green-400 px-4 h-1 rounded-md'
                            : 'bg-gray-400 px-4 h-1 rounded-md'
                    }
                  ></div>
                  <div
                    className={
                      strengthScore >= 2 && strengthScore < 4
                        ? 'bg-yellow-400 px-4 h-1 rounded-md'
                        : strengthScore === 4
                          ? 'bg-green-400 px-4 h-1 rounded-md'
                          : 'bg-gray-400 px-4 h-1 rounded-md'
                    }
                  ></div>
                  <div
                    className={
                      strengthScore >= 3 && strengthScore < 4
                        ? 'bg-yellow-400 px-4 h-1 rounded-md'
                        : strengthScore === 4
                          ? 'bg-green-400 px-4 h-1 rounded-md'
                          : 'bg-gray-400 px-4 h-1 rounded-md'
                    }
                  ></div>
                  <div
                    className={
                      strengthScore === 4
                        ? 'bg-green-400 px-4 h-1 rounded-md'
                        : 'bg-gray-400 px-4 h-1 rounded-md'
                    }
                  ></div>
                </div>
              </div>
              {/* Password Validation Conditions */}
              <div className="mt-4 text-md font-medium">
                <ul>
                  <li
                    className={
                      password.length >= 8 ? 'text-black' : 'text-[#BEBEBE]'
                    }
                  >
                    {password.length >= 8 ? (
                      <img
                        src="/tick-circle.svg"
                        alt="check"
                        className="w-5 h-5 inline mr-2 text-green-500"
                      />
                    ) : (
                      <img
                        src="/tick-circle-empty.svg"
                        alt="cross"
                        className="w-5 h-5 inline mr-2 text-red-500"
                      />
                    )}
                    At least 8 characters
                  </li>
                  <li
                    className={
                      /(?=.*[0-9])/.test(password)
                        ? 'text-black'
                        : 'text-[#BEBEBE]'
                    }
                  >
                    {/(?=.*[0-9])/.test(password) ? (
                      <img
                        src="/tick-circle.svg"
                        alt="check"
                        className="w-5 h-5 inline mr-2 text-green-500"
                      />
                    ) : (
                      <img
                        src="/tick-circle-empty.svg"
                        alt="cross"
                        className="w-5 h-5 inline mr-2 text-red-500"
                      />
                    )}
                    At least 1 number
                  </li>
                  <li
                    className={
                      /(?=.*[!@#$%^&*()_+={}\[\]|:;"'<>,.?/`~\\/-])/.test(
                        password,
                      )
                        ? 'text-black'
                        : 'text-[#BEBEBE]'
                    }
                  >
                    {/(?=.*[!@#$%^&*()_+={}\[\]|:;"'<>,.?/`~\\/-])/.test(
                      password,
                    ) ? (
                      <img
                        src="/tick-circle.svg"
                        alt="check"
                        className="w-5 h-5 inline mr-2 text-green-500"
                      />
                    ) : (
                      <img
                        src="/tick-circle-empty.svg"
                        alt="cross"
                        className="w-5 h-5 inline mr-2 text-red-500"
                      />
                    )}
                    At least 1 special character
                  </li>
                  <li
                    className={
                      /(?=.*[a-z])/.test(password) &&
                      /(?=.*[A-Z])/.test(password)
                        ? 'text-black'
                        : 'text-[#BEBEBE]'
                    }
                  >
                    {/(?=.*[a-z])/.test(password) &&
                    /(?=.*[A-Z])/.test(password) ? (
                      <img
                        src="/tick-circle.svg"
                        alt="check"
                        className="w-5 h-5 inline mr-2 text-green-500"
                      />
                    ) : (
                      <img
                        src="/tick-circle-empty.svg"
                        alt="cross"
                        className="w-5 h-5 inline mr-2 text-red-500"
                      />
                    )}
                    Both upper and lower case characters
                  </li>
                </ul>
              </div>
            </div>
            <div className="mb-4 relative">
              <label
                htmlFor="confirmpassword"
                className="block color-test dark:text-gray-300 font-normal color-test text-md mb-1"
              >
                Confirm Password
              </label>
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                required={true}
                id="confirmpassword"
                onChange={(e) => setConfirmPassword(e.target.value)}
                className={`h-[2.813rem] rounded-lg w-full py-2 px-3 text-black text-base font-medium leading-tight focus:outline-none  ${
                  confirmPassword != '' && password != confirmPassword
                    ? 'border-[#FF5050] border-2'
                    : 'border'
                }`}
                placeholder="Re-Type password"
              />
              <button
                type="button"
                onClick={toggleConfirmPassword}
                className="absolute right-3 set-top"
              >
                {showConfirmPassword ? (
                  <img src="/eye.svg" alt="Logo" className="h-auto" />
                ) : (
                  <img src="/eye-slash.svg" alt="Logo" className="h-auto" />
                )}
              </button>
              {confirmPassword != '' && password != confirmPassword && (
                <label
                  htmlFor="confirmpassword"
                  className="flex mt-1 items-center font-normal text-xs text-[#FF5050] mb-1"
                >
                  <img
                    src="/danger.svg"
                    alt="alert Icon"
                    className="h-4 w-4 mr-2"
                  />
                  Please make sure your passwords match.
                </label>
              )}
            </div>
            <button
              type="submit"
              disabled={isValid ? false : true}
              className={`w-full dark:bg-[#424242] dark:hover:bg-[#424242e6] font-medium text-md py-3  flex items-center justify-center gap-2 hover:bg-white-600 transition duration-300 ${
                isValid
                  ? 'bg-[#375AE7] text-white rounded-full'
                  : 'bg-[#D3D3D3] rounded-full text-[#9C9C9C]'
              }`}
            >
              Change Password
            </button>
          </form>
          <div className="mt-6 mb-4 max-w-sm w-full text-center">
            <img src="/line.svg" alt="Logo" className="h-auto" />
            <button
              onClick={() => {
                navigate('/login');
                dispatch(clearForcedPassword());
              }}
              type="submit"
              className="w-full mt-10 dark:bg-[#424242] dark:hover:bg-[#424242e6] bg-white rounded-full text-black font-medium text-md py-3 border-2 border-black flex items-center justify-center gap-2 hover:bg-white-600 transition duration-300"
            >
              <img
                src="/arrow-left-password.svg"
                alt="Icon"
                className="w-4 h-4"
              />
              Back To Log in
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChangePassword;
